import type {
  SlideData,
  ContentSlideData,
  QuizSlideData,
  SpeakSlideData,
  ContentElement,
} from "@/types/lesson";

const MAX_TTS_LEN = 900;

/**
 * Remove LaTeX/math delimiters for TTS. Inner text remains (e.g. `x^2 + 3` may be read naturally).
 * Handles `\(...\)`, `\[...\]`, `$...$`, `$$...$$`.
 */
export function stripMath(text: string): string {
  if (!text) return "";
  return text
    .replace(/\\\[([\s\S]*?)\\\]/g, (_, inner: string) => inner.trim())
    .replace(/\\\(([\s\S]*?)\\\)/g, (_, inner: string) => inner.trim())
    .replace(/\$\$([\s\S]*?)\$\$/g, (_, inner: string) => inner.trim())
    .replace(/\$([^$\n]+)\$/g, (_, inner: string) => inner.trim());
}

/** Strip math delimiters for TTS (narration and built slide scripts). */
export function plainTextForTts(s: string): string {
  return stripMath(s);
}

/** Safe string for any step item — never "[object Object]" */
export function stepItemText(item: unknown): string {
  if (item == null) return "";
  if (typeof item === "string") return item;
  if (typeof item === "object" && item !== null && "text" in item) {
    const t = (item as { text?: unknown }).text;
    return t == null ? "" : String(t);
  }
  return String(item);
}

export function quizOptionLabel(opt: unknown): string {
  if (opt == null) return "";
  if (typeof opt === "string") return opt;
  if (typeof opt === "object" && opt !== null && "text" in opt) {
    const t = (opt as { text?: unknown }).text;
    return t == null ? "" : String(t);
  }
  return String(opt);
}

function buildStepsTts(items: unknown[] | undefined, title?: string): string {
  if (!items?.length) return "";
  const stepTexts = items.map((item, i) => {
    const txt = plainTextForTts(stepItemText(item));
    return txt ? `Step ${i + 1}: ${txt}` : "";
  }).filter(Boolean);
  if (!stepTexts.length) return "";
  const prefix = title ? `${plainTextForTts(title)}. ` : "Here are the steps. ";
  return prefix + stepTexts.join(". ") + ".";
}

function buildContentElementTts(el: ContentElement): string {
  switch (el.type) {
    case "text":
      return plainTextForTts(el.content || "");
    case "term":
      return `The word "${plainTextForTts(el.term)}" means: ${plainTextForTts(el.definition)}.`;
    case "example":
      return (
        (el.label ? `${plainTextForTts(el.label)}. ` : "") +
        "Here is an example. " +
        plainTextForTts(el.content || "")
      );
    case "tip":
      return "Tip: " + plainTextForTts(el.text || "");
    case "steps":
      return buildStepsTts(el.items as unknown[], el.title);
    case "bar_chart":
    case "line_chart":
      return plainTextForTts([el.title, ...el.labels].filter(Boolean).join(". "));
    case "pie_chart":
      return plainTextForTts([el.title, ...el.labels].filter(Boolean).join(". "));
    case "comparison_table":
      return plainTextForTts([el.title, ...el.headers, ...el.rows.flat()].filter(Boolean).join(". "));
    case "process_flow":
      return plainTextForTts(
        [el.title, ...el.steps.map((s) => `${s.label}. ${s.detail}`)].filter(Boolean).join(". ")
      );
    case "labeled_diagram":
      return plainTextForTts(
        [el.title, el.description, ...el.parts.map((p) => `${p.label}: ${p.detail}`)]
          .filter(Boolean)
          .join(". ")
      );
    default:
      return "";
  }
}

function buildContentScript(s: ContentSlideData): string {
  const parts: string[] = [];
  if (s.narration) parts.push(plainTextForTts(s.narration));
  if (s.elements) {
    for (const el of s.elements) {
      const t = buildContentElementTts(el);
      if (t) parts.push(t);
    }
  }
  return parts.filter(Boolean).join(" ");
}

function buildQuizScript(s: QuizSlideData): string {
  const parts: string[] = [];
  if (s.narration) parts.push(plainTextForTts(s.narration));
  if (s.question) parts.push("Here is the question: " + plainTextForTts(s.question));
  if (s.hint) parts.push("A clue: " + plainTextForTts(s.hint));
  if (s.options?.length) {
    const letters = ["A", "B", "C", "D", "E", "F"];
    const opts = s.options
      .map((o, i) => `${letters[i] ?? "?"}: ${plainTextForTts(quizOptionLabel(o))}`)
      .join(". ");
    parts.push("Your options are: " + opts);
  }
  return parts.filter(Boolean).join(" ");
}

function buildSpeakScript(s: SpeakSlideData): string {
  const parts: string[] = [];
  if (s.narration) parts.push(plainTextForTts(s.narration));
  if (s.prompt) parts.push(plainTextForTts(s.prompt));
  if (s.phrase) parts.push(plainTextForTts(s.phrase) + ".");
  return parts.filter(Boolean).join(" ");
}

export function buildSlideTtsScript(slide: SlideData | null): string {
  if (!slide) return "";
  let out = "";
  if (slide.type === "content") out = buildContentScript(slide);
  else if (slide.type === "quiz") out = buildQuizScript(slide);
  else if (slide.type === "speak") out = buildSpeakScript(slide);
  out = out.trim();
  if (out.length > MAX_TTS_LEN) return out.substring(0, MAX_TTS_LEN);
  return out;
}
